import React, { useEffect } from 'react';
import { Button, ButtonV2, Modal } from '@asigna/kit';
import { createSubscriptionOrder, getAllSubscriptionOrders } from 'api';
import axios from 'axios';
import cx from 'classnames';
import { NotificationCard } from 'components';
import { useIsProActivated, useReadOnlyWarning } from 'hooks';
import moment from 'moment';
import { toast } from 'sonner';
import { useParrentSafe } from 'store';
import { Tab } from 'ui-kit';

import { SUBSCRIPTION_STATUSES_MAP } from './util/constants';
import checkIcon from './util/images/check.svg';
import goldRoundIcon from './util/images/goldround.svg';
import roundIcon from './util/images/round.svg';
import { useProgressPro } from './util/useProgressPro';
import { useProPage } from './util/useProPage';
import {
  CircularProgress,
  CompleteOrder,
  ExtendSubscriptionDialog,
  PeriodButton,
} from './components';

import s from './index.module.scss';

export const ProPage: React.FC = () => {
  const {
    isLoading,
    setIsLoading,
    tabs,
    selectedTab,
    setSelectedTab,
    BasicPlan,
    ProPlan,
    periodOptions,
    selectedPeriod,
    setSelectedPeriod,
    minMonthPrice,
    orders,
    setOrders,
    orderToPay,
    setOrderToPay,
    products,
    isModalOpen,
    setIsModalOpen,
  } = useProPage();
  const readOnlyWarning = useReadOnlyWarning();
  const currentSafe = useParrentSafe();
  const isActivatedPro = useIsProActivated();

  useEffect(() => {
    if (!currentSafe || readOnlyWarning) return;
    getAllSubscriptionOrders(currentSafe._id).then((res) => setOrders(res.reverse()));
  }, [currentSafe]);
  const parrentSafe = useParrentSafe();

  const { progress, daysLeft } = useProgressPro(
    parrentSafe?.actualSubscriptionStartTime?.toString() ?? '',
    parrentSafe?.actualSubscriptionEndTime?.toString() ?? ''
  );

  const createOrder = async () => {
    if (!currentSafe || !products) return;

    if (isModalOpen) {
      setIsModalOpen(false);
    }

    setIsLoading(true);
    try {
      await createSubscriptionOrder({
        multiSigId: currentSafe._id,
        subscriptionRate: products.find((product) => product.name === selectedPeriod)?._id || '',
      });
      getAllSubscriptionOrders(currentSafe._id).then((res) => setOrders(res.reverse()));
      toast(
        () => (
          <NotificationCard
            title="Subscription success"
            message="Order added successfully"
            type="success"
          />
        ),
        {
          className: 'w-full',
        }
      );

      setSelectedTab('Orders');
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        const backendMessage = error.response?.data?.message;
        if (backendMessage) {
          toast(
            () => (
              <NotificationCard
                title="Order creation error"
                message={backendMessage}
                type="error"
              />
            ),
            {
              className: 'w-full',
            }
          );
        } else {
          console.error('Error creating subscription order:', error.message);
        }
      } else {
        console.error('Unknown error:', error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex w-full pl-20 sm:pl-0 h-screen mb-32">
      <div className="flex flex-col gap-24 w-full relative pr-20 sm:pr-32 py-16 overflow-y-scroll">
        {orderToPay && (
          <CompleteOrder
            open
            onClose={() => setOrderToPay(undefined)}
            order={orderToPay}
            onDone={() => {
              // TODO
            }}
          />
        )}
        {isActivatedPro && (
          <div
            className={cx(
              s.bnnr,
              'flex flex-col sm:flex-row gap-16 sm:gap-4 rounded-[16px] bg-white-10 px-20 sm:px-32 py-24 items-center justify-between'
            )}
          >
            <div className="flex gap-24 items-center">
              <CircularProgress days={daysLeft} progress={progress} />

              <div className="flex flex-col gap-16">
                <h2 className="text-r16 sm:text-r20 text-white">Asigna PRO is Active!</h2>
                <span className="text-r14 text-white">
                  Until {moment(currentSafe?.actualSubscriptionEndTime).format('MMM D, YYYY')}
                </span>
              </div>
            </div>
            <Button
              disabled={Boolean(readOnlyWarning) || isLoading}
              leftIcon={isLoading ? '/loading-gif.gif' : undefined}
              onClick={() => setIsModalOpen(true)}
              message="Extend Subscription"
              colorType="white"
              size="md"
              fontColor="black-0"
            />
          </div>
        )}

        <div className="flex gap-8">
          {tabs.map((tab) => {
            return (
              <Tab
                id={tab}
                key={tab}
                disabled={tab === 'Orders' && Boolean(readOnlyWarning)}
                isActive={tab === selectedTab}
                onClick={setSelectedTab}
              />
            );
          })}
        </div>
        {selectedTab === 'Subscription' && (
          <div className="flex flex-col items-center gap-32 mb-48 sm:mb-16">
            {!isActivatedPro && (
              <h1 className="text-r24">Get more opportunities with Asigna Pro!</h1>
            )}
            <div className="flex flex-col-reverse sm:flex-row items-center justify-center w-full">
              <div className="flex flex-col gap-16 p-24 pr-16 bg-black-1 rounded-[16px] w-full sm:max-w-[309px] sm:h-[92%]">
                <div className="flex items-center justify-between">
                  <h3 className="text-r20 text-white">Basic</h3>
                  <div className="flex items-center justify-center h-28 w-56 bg-white-10 rounded-[24px] text-r12 text-regular">
                    Free
                  </div>
                </div>
                <div className="flex flex-col gap-24">
                  {BasicPlan.map((item) => (
                    <div key={item} className="flex items-center gap-12">
                      <img src={roundIcon} alt="" />
                      <span className="text-r14">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-24 p-24 rounded-[16px] border-[.5px] border-[#FFE341] bg-[linear-gradient(91deg,_#1C190C_0%,_#14100C_100%)] w-full sm:max-w-[341px]">
                <div className="flex items-center justify-between">
                  <h3 className="text-r20 text-white">Asigna Pro</h3>
                  <div className="flex items-center justify-center h-28 w-100 bg-white-10 rounded-[24px] text-r12 text-white">
                    From ${minMonthPrice}/mo
                  </div>
                </div>
                <div className="flex flex-col gap-24">
                  {ProPlan.map((item, index) =>
                    index === 0 ? (
                      <div
                        key={item}
                        className="flex items-center gap-12 bg-success-10 rounded-[20px] py-4 pl-8 pr-12"
                      >
                        <img src={checkIcon} alt="" />
                        <span className="text-r14">{item}</span>
                      </div>
                    ) : (
                      <div key={item} className="flex items-center gap-12">
                        <img src={goldRoundIcon} alt="" />
                        <span className="text-r14">{item}</span>
                      </div>
                    )
                  )}
                </div>
                <div className="bg-white-10 w-full h-[.5px]" />
                <div className="flex flex-col gap-8">
                  <h4 className="text-m14">Choose period</h4>
                  <div className="grid grid-cols-2 gap-8">
                    {periodOptions?.map((period, index) => (
                      <PeriodButton
                        key={period.title}
                        title={period.title}
                        price={period.price}
                        fullPrice={period.fullPrice}
                        discount={period.discount}
                        isLast={index === periodOptions.length - 1}
                        isSelected={period.title === selectedPeriod}
                        onClick={() => setSelectedPeriod(period.title)}
                      />
                    ))}
                  </div>
                  <ButtonV2
                    className="mt-12 w-full bg-[linear-gradient(103deg,_#FFE341_0%,_#F7E47D_24.5%,_#F1C855_48.99%,_#FFE2A8_73.49%,_#FFE970_97.99%)] text-[#493B15] font-extrabold"
                    disabled={Boolean(readOnlyWarning) || isLoading}
                    onClick={createOrder}
                    disableHint={readOnlyWarning}
                  >
                    {isLoading ? (
                      <img src="/loading-gif.gif" alt="" width={16} height={16} />
                    ) : (
                      <span className="text-[#493B15]">{`${
                        isActivatedPro ? 'Extend' : 'Buy'
                      } for ${selectedPeriod}`}</span>
                    )}
                  </ButtonV2>
                </div>
              </div>
            </div>
          </div>
        )}
        {selectedTab === 'Orders' && (
          <div className="flex flex-col w-full mb-48 sm:mb-8">
            <div className="hidden sm:grid grid-cols-[29%_20%_20%_12%_11%_8%] text-m13 text-[#949494] font-semibold items-center mb-8">
              <div>Order id</div>
              <div>Date</div>
              <div>Amount</div>
              <div>Subscription</div>
              <div>Status</div>
              <div />
            </div>
            {orders?.map((order) => {
              return (
                <div
                  key={order._id}
                  className="flex flex-wrap gap-y-[8px] justify-between sm:grid grid-cols-[29%_20%_20%_12%_11%_8%] py-16 text-m13 items-center border-b border-white-10"
                >
                  <div className="flex items-center gap-4 text-r14 overflow-hidden text-ellipsis whitespace-nowrap">
                    {order._id}
                  </div>
                  <div className="flex items-center gap-4 text-r14 overflow-hidden text-ellipsis whitespace-nowrap">
                    {moment(order.creationTime).format('MMM D, YYYY, h:mm A')}
                  </div>
                  <div className="flex items-center gap-4 text-r14">
                    <span className="text-white">
                      {Number(order.stacksTotalPrice) / Math.pow(10, 6)} STX
                    </span>
                    <div className="w-4 h-4 rounded-[4px] bg-regular" />
                    <span className="text-regular">${order.usdPrice}</span>
                  </div>
                  <div className="flex items-center gap-4 text-r14 overflow-hidden text-ellipsis whitespace-nowrap">
                    {products?.find((product) => product._id === order.subscriptionRate)?.name}
                  </div>
                  <div className="flex items-center gap-4">
                    <span
                      className={cx(
                        'py-4 px-8 rounded-[4px] text-r12',
                        order.status === 'CREATED' && 'bg-primary-10 text-primary',
                        order.status === 'CANCELLED' && 'bg-error-10 text-error',
                        order.status === 'PAID' && 'bg-success-10 text-success',
                        order.status === 'ARCHIVED' && 'bg-white-10 text-regular'
                      )}
                    >
                      {SUBSCRIPTION_STATUSES_MAP[order.status]}
                    </span>
                  </div>
                  <div
                    className={cx(
                      'flex items-center gap-4 justify-end',
                      order.status !== 'CREATED' && order.status !== 'CANCELLED' && 'hidden sm:flex'
                    )}
                  >
                    {order.status === 'CREATED' && (
                      <Button
                        onClick={() => setOrderToPay(order)}
                        message="Pay"
                        colorType="primary"
                        size="md"
                      />
                    )}
                    {order.status === 'CANCELLED' && (
                      <Button message="Contact us" colorType="secondary" size="md" />
                    )}
                  </div>
                </div>
              );
            })}
            {orders && !orders.length && <div>No orders yet</div>}
          </div>
        )}
      </div>
      <Modal isShow={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <ExtendSubscriptionDialog
          onClose={() => setIsModalOpen(false)}
          onSuccess={createOrder}
          selectedPeriod={selectedPeriod}
          setSelectedPeriod={setSelectedPeriod}
        />
      </Modal>
    </div>
  );
};
